/**
 * Tutorial - Interactive first-time tutorial for new players
 *
 * Shows a small tooltip panel at the bottom-center of the screen
 * guiding the player through basic controls step by step.
 * Only activates the first time a player opens the game.
 * Uses localStorage flag 'fedeworld_tutorial_done' to track completion.
 */

const STORAGE_KEY = 'fedeworld_tutorial_done';

const STEPS = [
  { id: 'move',      text: 'Mueve con WASD' },
  { id: 'look',      text: 'Mira alrededor con el ratón' },
  { id: 'break',     text: 'Rompe un bloque (click izquierdo)' },
  { id: 'inventory', text: 'Abre el inventario (E)' },
  { id: 'place',     text: 'Coloca un bloque (click derecho)' },
  { id: 'done',      text: '¡Listo! Explora y craftea herramientas' },
];

export class Tutorial {
  constructor() {
    this._active = false;
    this._currentStep = 0;
    this._container = null;
    this._textEl = null;
    this._stepEl = null;
    this._lookTimer = null;
    this._doneTimer = null;
    this._onKeyDown = null;

    // Check if tutorial was already completed
    if (localStorage.getItem(STORAGE_KEY)) {
      return;
    }

    this._active = true;
    this._build();
    this._showStep(0);
    this._bindEscape();
  }

  /**
   * Build the tutorial DOM elements.
   */
  _build() {
    // Inject keyframes for fade-in animation
    const style = document.createElement('style');
    style.id = 'tutorial-style';
    style.textContent = `
      @keyframes tutorialFadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);

    // Main container
    this._container = document.createElement('div');
    this._container.id = 'tutorial-panel';
    this._container.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 800;
      background: rgba(0, 0, 0, 0.7);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      padding: 12px 20px;
      font-family: 'Courier New', monospace;
      pointer-events: auto;
      min-width: 280px;
      text-align: center;
      animation: tutorialFadeIn 0.4s ease-out;
    `;

    // Step counter (e.g. "1/6")
    this._stepEl = document.createElement('div');
    this._stepEl.style.cssText = `
      font-size: 10px;
      color: rgba(255, 255, 255, 0.4);
      margin-bottom: 4px;
      letter-spacing: 1px;
    `;
    this._container.appendChild(this._stepEl);

    // Main instruction text
    this._textEl = document.createElement('div');
    this._textEl.style.cssText = `
      font-size: 14px;
      color: #FFFFFF;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6);
      margin-bottom: 10px;
    `;
    this._container.appendChild(this._textEl);

    // Skip button
    const skipBtn = document.createElement('button');
    skipBtn.textContent = 'Saltar Tutorial';
    skipBtn.style.cssText = `
      background: none;
      border: 1px solid rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.5);
      font-family: 'Courier New', monospace;
      font-size: 10px;
      padding: 3px 10px;
      border-radius: 3px;
      cursor: pointer;
      transition: color 0.2s, border-color 0.2s;
    `;
    skipBtn.addEventListener('mouseenter', () => {
      skipBtn.style.color = 'rgba(255, 255, 255, 0.9)';
      skipBtn.style.borderColor = 'rgba(255, 255, 255, 0.5)';
    });
    skipBtn.addEventListener('mouseleave', () => {
      skipBtn.style.color = 'rgba(255, 255, 255, 0.5)';
      skipBtn.style.borderColor = 'rgba(255, 255, 255, 0.25)';
    });
    skipBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.skip();
    });
    this._container.appendChild(skipBtn);

    document.body.appendChild(this._container);
  }

  /**
   * Bind the Escape key to skip the tutorial.
   */
  _bindEscape() {
    this._onKeyDown = (e) => {
      if (e.key === 'Escape' && this._active) {
        this.skip();
      }
    };
    window.addEventListener('keydown', this._onKeyDown);
  }

  /**
   * Display a specific step with a fade-in animation.
   * @param {number} index
   */
  _showStep(index) {
    if (!this._active || !this._container) return;
    this._currentStep = index;

    const step = STEPS[index];
    this._stepEl.textContent = `${index + 1} / ${STEPS.length}`;
    this._textEl.textContent = step.text;

    // Replay fade-in animation
    this._container.style.animation = 'none';
    // Force reflow to restart animation
    void this._container.offsetHeight;
    this._container.style.animation = 'tutorialFadeIn 0.4s ease-out';

    // Auto-advance for the 'look' step (2 seconds)
    if (step.id === 'look') {
      this._lookTimer = setTimeout(() => {
        this._advanceStep();
      }, 2000);
    }

    // Auto-advance for the 'done' step (3 seconds)
    if (step.id === 'done') {
      this._doneTimer = setTimeout(() => {
        this._complete();
      }, 3000);
    }
  }

  /**
   * Advance to the next step, or complete if at the end.
   */
  _advanceStep() {
    if (!this._active) return;
    const next = this._currentStep + 1;
    if (next >= STEPS.length) {
      this._complete();
    } else {
      this._showStep(next);
    }
  }

  /**
   * Mark the tutorial as complete and clean up.
   */
  _complete() {
    if (!this._active) return;
    localStorage.setItem(STORAGE_KEY, '1');
    this._active = false;
    this._cleanup();
  }

  /**
   * Remove all DOM elements and timers.
   */
  _cleanup() {
    if (this._lookTimer) {
      clearTimeout(this._lookTimer);
      this._lookTimer = null;
    }
    if (this._doneTimer) {
      clearTimeout(this._doneTimer);
      this._doneTimer = null;
    }
    if (this._onKeyDown) {
      window.removeEventListener('keydown', this._onKeyDown);
      this._onKeyDown = null;
    }
    if (this._container) {
      // Fade out before removing
      this._container.style.transition = 'opacity 0.3s';
      this._container.style.opacity = '0';
      setTimeout(() => {
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container);
        }
        this._container = null;
        this._textEl = null;
        this._stepEl = null;
      }, 300);
    }
    const styleEl = document.getElementById('tutorial-style');
    if (styleEl) {
      styleEl.parentNode.removeChild(styleEl);
    }
  }

  // --- Public API ---

  /**
   * Call when the player moves (WASD).
   */
  onPlayerMove() {
    if (!this._active) return;
    if (STEPS[this._currentStep].id === 'move') {
      this._advanceStep();
    }
  }

  /**
   * Call when a block is broken (left click).
   */
  onBlockBroken() {
    if (!this._active) return;
    if (STEPS[this._currentStep].id === 'break') {
      this._advanceStep();
    }
  }

  /**
   * Call when the inventory is opened (E key).
   */
  onInventoryOpened() {
    if (!this._active) return;
    if (STEPS[this._currentStep].id === 'inventory') {
      this._advanceStep();
    }
  }

  /**
   * Call when a block is placed (right click).
   */
  onBlockPlaced() {
    if (!this._active) return;
    if (STEPS[this._currentStep].id === 'place') {
      this._advanceStep();
    }
  }

  /**
   * Whether the tutorial is currently active.
   * @returns {boolean}
   */
  isActive() {
    return this._active;
  }

  /**
   * Skip the tutorial entirely.
   */
  skip() {
    this._complete();
  }

  /**
   * Full cleanup - call if the game needs to tear down the tutorial.
   */
  dispose() {
    this._active = false;
    this._cleanup();
  }
}
